import { httpsCallable } from 'firebase/functions';

import { getFirebaseFunctions } from '@/lib/firebase/functions';
import { resolveBrowserQueryCache, type BrowserQueryCache } from '@/lib/query-cache/query-cache';

export interface CandidateProposalDocumentResponse {
  slug: string;
  url: string;
  expiresAt: string;
  filePath: string;
  contentType: string;
}

type ResolveCandidateProposalDocumentRequest = {
  slug: string;
};

const persistentCachePrefix = 'candidate-proposal-document-url:v1';

function getPersistentCacheKey(slug: string): string {
  return `${persistentCachePrefix}:${slug}`;
}

function readPersistentCache(slug: string): CandidateProposalDocumentResponse | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = window.localStorage.getItem(getPersistentCacheKey(slug));

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as CandidateProposalDocumentResponse;

    if (!parsed.url || !parsed.expiresAt || Date.parse(parsed.expiresAt) <= Date.now()) {
      window.localStorage.removeItem(getPersistentCacheKey(slug));
      return null;
    }

    return parsed;
  } catch {
    window.localStorage.removeItem(getPersistentCacheKey(slug));
    return null;
  }
}

function writePersistentCache(response: CandidateProposalDocumentResponse): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(getPersistentCacheKey(response.slug), JSON.stringify(response));
}

export async function getCandidateProposalDocument(
  slug: string,
  options?: { cache?: BrowserQueryCache },
): Promise<CandidateProposalDocumentResponse> {
  const cached = readPersistentCache(slug);

  if (cached) {
    return cached;
  }

  const cache = resolveBrowserQueryCache(options?.cache);
  const callable = httpsCallable<ResolveCandidateProposalDocumentRequest, CandidateProposalDocumentResponse>(
    getFirebaseFunctions(),
    'resolveCandidateProposalDocument',
  );

  const result = await cache.read(
    ['candidate-proposal-document', slug],
    async () => {
      const response = await callable({ slug });
      writePersistentCache(response.data);
      return response.data;
    },
    {
      staleTimeMs: 55 * 60 * 1000,
      cacheTimeMs: 60 * 60 * 1000,
      tags: ['candidate-proposal-document'],
    },
  );

  return result.value;
}
